/**
 * The single place a "holiday" AttendanceRecord is turned into the label a
 * user sees - "Holiday - Diwali", "Working on Holiday", "Worked on
 * Holiday" - so the ESS dashboard's Today card, the check-in card, and My
 * Attendance's history table can never disagree about what the exact same
 * record means. Every other status keeps rendering through the generic
 * StatusBadge unchanged; only "holiday" needs a richer, three-state label
 * (not worked / working / worked), which the backend intentionally
 * expresses through the SAME status plus first_in/last_out rather than a
 * separate enum value - see backend domain.Attendance's own doc comment on
 * holiday_name/holiday_work_reason.
 */
import type { AttendanceRecord } from "@/types/domain";

export type HolidayWorkState = "unworked" | "working" | "worked";

export interface HolidayDayDisplay {
  state: HolidayWorkState;
  /** "Holiday - Diwali" / "Working on Holiday" / "Worked on Holiday". */
  label: string;
  holidayName: string;
}

/** Returns null for anything that is not a holiday day at all - the caller
 * falls back to its normal status rendering (StatusBadge) in that case. */
export function describeHolidayDay(
  record: Pick<AttendanceRecord, "status" | "holiday_name" | "first_in" | "last_out"> | null | undefined,
): HolidayDayDisplay | null {
  if (!record || record.status !== "holiday") return null;
  const holidayName = record.holiday_name || "Holiday";
  if (!record.first_in) {
    return { state: "unworked", label: `Holiday – ${holidayName}`, holidayName };
  }
  if (!record.last_out) {
    return { state: "working", label: "Working on Holiday", holidayName };
  }
  return { state: "worked", label: "Worked on Holiday", holidayName };
}
